import { decodeJwt } from "jose";
import { get_token } from "./ApiHelper";
import { get_package, get_user_agreements } from "./api.calls.services";

export const set_dimensions = () => {
  return {
    mobile: window.innerWidth < 950 ? true : false,
    desktop: window.innerWidth >= 950 ? true : false,
  };
};

export const format_date = (isoDate: any) => {
  const date = new Date(isoDate);
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

export const format_price = (price: number | string) => {
  let num = Number(price) || 0;

  // Truncate to 2 decimals instead of rounding
  num = Math.trunc(num * 100) / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export function decode_jwt(token: string | null) {
  if (!token) return null;
  try {
    return decodeJwt(token); // Returns a typed payload
  } catch (e) {
    console.error("Invalid JWT", e);
    return null;
  }
}

const sort_agreements = (agreements: { status: string }[]) => {
  const statusOrder: Record<string, number> = {
    active: 0,
    "past due": 1,
    frozen: 2,
    "scheduled for review": 3,
    "scheduled for cancellation": 4,
    cancelled: 5,
  };

  return [...agreements].sort((a, b) => {
    const orderA = statusOrder[a.status] ?? Number.MAX_SAFE_INTEGER;
    const orderB = statusOrder[b.status] ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
};

export const get_agreements = (dispatch: any, update_state_value: any) => {
  dispatch(update_state_value({ key: "app_loading", value: true }));
  // Reset any prior error flag at the start of a fetch so a retry can recover.
  dispatch(update_state_value({ key: "app_error", value: false }));
  const userToken = JSON.parse(localStorage.getItem("user_token") || "null");
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const clubId = JSON.parse(localStorage.getItem("clubId") || "null");

  const fetchAgreements = async (
    token: string,
    clubId: string,
    userId: string
  ) => {
    try {
      const data = await get_user_agreements(token, clubId, userId);
      const agreements = data.data;

      // Build an array of promises to resolve packages
      const agreementPromises = agreements.map(async (agreement: any) => {
        var { isFrozen } = agreement;
        // Set agreement status
        if (agreement.cancelDateUtc && !agreement?.isFrozen) {
          agreement.status = "cancelled";
        } else if (agreement.cancellationPendingReview) {
          agreement.status = "scheduled for review";
        } else if (
          (agreement.agreement_processing ||
            agreement.scheduledForCancellingOnUtc) &&
          !agreement?.isFrozen
        ) {
          agreement.status = "scheduled for cancellation";
        } else if (agreement?.isFrozen) {
          agreement.status = "frozen";
        } else {
          agreement.status = agreement.delinquentDate ? "past due" : "active";
        }

        agreement.frozen = isFrozen;

        // Fetch and assign package. The lookup is supplemental — it only
        // supplies a display name. A single failure must not discard the whole
        // list (dev APIM does not expose this operation at all; in prod a
        // deleted/moved package would 404 the same way).
        try {
          const packageData = await get_package(
            token,
            clubId,
            agreement.packageId
          );
          if (packageData?.name) {
            agreement.name = packageData.name;
            agreement.package = packageData;
          }
        } catch {
          // Fall through to the packageName the agreements payload already carries.
        }

        agreement.name = agreement.name || agreement.packageName;

        return agreement;
      });

      // Wait for all packages to be fetched and applied
      const updatedAgreements = await Promise.all(agreementPromises);

      // Sort and set. An empty result falls through here so the UI can render
      // its empty-state (empty is not an error).
      dispatch(
        update_state_value({
          key: "agreements",
          value: sort_agreements(updatedAgreements),
        })
      );
    } catch (e) {
      // Flag the failure so the UI can show a visible error-state instead of a
      // silent blank page. Do not log the error/response body (may carry PII).
      dispatch(update_state_value({ key: "app_error", value: true }));
    } finally {
      // Always clear the loading flag so the spinner never gets stuck on throw.
      dispatch(update_state_value({ key: "app_loading", value: false }));
    }
  };

  // fetchAgreements clears app_loading itself in its own finally. The
  // no-token path below never calls it, so the spinner has to be cleared
  // here too — otherwise app_loading stays true forever and the portal
  // spins indefinitely (Bug 69238).
  const stopLoading = () =>
    dispatch(update_state_value({ key: "app_loading", value: false }));

  if (userToken?.access_token) {
    // Use real user token and IDs
    const { access_token, refresh_token, expires_in } = userToken;
    const authToken = expires_in ? access_token : refresh_token;
    const userId = userToken.UserId || userToken.userId || user?.UserID;

    fetchAgreements(`bearer ${authToken}`, clubId, userId);
  } else {
    console.error("No token", userToken);
    stopLoading();
  }
};

export function formatPhoneNumber(phone: any) {
  // Remove all non-digit characters
  const cleaned = ("" + phone).replace(/\D/g, "");

  // Check if the input has exactly 10 digits
  if (cleaned.length !== 10) {
    return null; // or return the original input, or throw an error
  }

  const areaCode = cleaned.slice(0, 3);
  const firstPart = cleaned.slice(3, 6);
  const secondPart = cleaned.slice(6, 10);

  return `(${areaCode}) ${firstPart}-${secondPart}`;
}

export const normalizeUrl = (url: any) => {
  if (!url) return "";

  try {
    // If the URL already has http:// or https://, leave it as is
    const hasProtocol = /^https?:\/\//i.test(url);
    return hasProtocol ? url : `https://${url}`;
  } catch (err) {
    console.error("Invalid URL:", url);
    return "";
  }
};

export const handleTokenExpired = (tokenData: any) => {
  const now = Date.now();
  const expires = tokenData?.exp ? tokenData.exp * 1000 : null;

  if (!expires || expires < now) {
    window.location.reload();
  } else {
    setTimeout(() => window.location.reload(), expires - now);
  }
};

// Cr's Verification page posts `userToken: { }` (a literal empty object) whenever
// it is reached without an auth_code — which is the normal member entry path, since
// OnlineCancel/Index redirects there with only Sid. An empty object is truthy, so
// truthiness alone would take the logged-in path and fetch user `undefined`.
export const hasUsableUserToken = (rawUserToken: any): boolean =>
  !!rawUserToken?.access_token;

export const getValidAuthToken = (rawUserToken: any): string => {
  const now = Math.floor(Date.now() / 1000);
  const userJwt: any = decode_jwt(rawUserToken.access_token);

  const expiresIn = rawUserToken.expires_in;
  const receivedAt = now;
  const expiresAt = receivedAt + expiresIn;

  return now < expiresAt
    ? rawUserToken.access_token
    : rawUserToken.refresh_token;
};

export function getFormattedCancelDate(
  contract: {
    installmentsRemaining?: number;
    lastInstallmentDueDateUtc?: string;
  },
  isHttp: boolean = false,
  noticePeriodDays: number
): string | Date {
  const isInTerm = !!contract.installmentsRemaining;

  let baseDate: Date;

  if (isInTerm && contract.lastInstallmentDueDateUtc) {
    // In-term: start from the day after last installment due date (ignore notice period)
    baseDate = new Date(contract.lastInstallmentDueDateUtc);
    baseDate.setUTCDate(baseDate.getUTCDate() + 1);
  } else {
    // Out-of-term: start from today + notice period (if truthy)
    const noticePeriod = noticePeriodDays || 1;
    baseDate = new Date();
    baseDate.setUTCDate(baseDate.getUTCDate() + noticePeriod);
  }

  return isHttp ? baseDate : format_date(baseDate);
}

export function adjustDateIfInTerm(baseDate: any, inTerm: boolean = false) {
  const newDate = new Date(baseDate);

  if (inTerm) {
    newDate.setUTCDate(newDate.getUTCDate() + 1);
  }

  const month = String(newDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(newDate.getUTCDate()).padStart(2, "0");
  const year = newDate.getUTCFullYear();

  return `${month}/${day}/${year}`;
}